import {useRevaTranslation} from "../hooks/languageHooks";
import {useRevaSelector} from "../redux/revaHooks";
import {
    LAYER_ALKIS,
    LAYER_BASEMAP,
    LAYER_POIS_DECORATED,
    LAYER_WOHNLAGEN_MIKRO_DECORATED_BACKGROUND,
    LAYER_WOHNLAGEN_MIKRO_DECORATED_TOP,
    selectLayerErrors,
    type WmtsLayer,
} from "../redux/wmtsSlice";

/** i18n-Schlüssel (unter `general.map.layerName`) für die Bezeichnung eines Layers im Hinweis. */
const LAYER_NAME_KEYS: Partial<Record<WmtsLayer, string>> = {
    [LAYER_BASEMAP]: "basemap",
    [LAYER_ALKIS]: "alkis",
    [LAYER_POIS_DECORATED]: "pois",
    [LAYER_WOHNLAGEN_MIKRO_DECORATED_BACKGROUND]: "wohnlagen",
    [LAYER_WOHNLAGEN_MIKRO_DECORATED_TOP]: "wohnlagen",
};

interface MapLayerUnavailableHintProps {
    /** die Layer, die diese Karte nutzt; nur deren Ausfall wird gemeldet */
    layers: WmtsLayer[];
    /**
     * true: als kleines Banner oben über der Karte (der Container muss `relative` sein).
     * false: als Zeile im normalen Fluss, z.B. wenn die Karte ganz entfällt.
     */
    overlay?: boolean;
}

/**
 * Kleiner Hinweis, dass einzelne Kartenebenen gerade nicht geladen werden konnten (#19906). Die Karte
 * selbst wird mit den übrigen Ebenen gezeichnet; der Hinweis erklärt nur, was fehlt.
 * Rendert nichts, solange alle genannten Layer verfügbar sind.
 */
export default function MapLayerUnavailableHint({layers, overlay = true}: MapLayerUnavailableHintProps) {
    const {t} = useRevaTranslation();
    const layerErrors = useRevaSelector(selectLayerErrors);

    const names = [
        ...new Set(
            layers
                .filter((layer) => layerErrors[layer])
                .map((layer) => t(`general.map.layerName.${LAYER_NAME_KEYS[layer] ?? "generic"}`)),
        ),
    ];
    if (names.length === 0) return null;

    const text = t("general.map.layerUnavailable", {layers: names.join(", ")});
    const className = overlay
        ? "absolute top-2 left-1/2 -translate-x-1/2 z-10 max-w-[90%] pointer-events-none"
        : "w-full my-2";

    return (
        <div className={className} role="status" data-testid="map-layer-unavailable-hint">
            <div className="flex items-center gap-2 px-3 py-1 text-sm rounded-border shadow-md bg-yellow-50 text-yellow-900 border border-yellow-300 dark:bg-yellow-900 dark:text-yellow-50 dark:border-yellow-700">
                <i className="pi pi-exclamation-triangle" aria-hidden="true" />
                <span>{text}</span>
            </div>
        </div>
    );
}
